import { useLocation } from 'react-router-dom'

export type Brand = 'broxel' | 'pharma4link'

/**
 * Routes that belong to the Pharma4Link platform rather than the Broxel
 * corporate site. Pharma4Link moves to its own subdomain later; when it does,
 * this list is deleted and `brandForPath` becomes `brandForHost`. That is the
 * entire reason the boundary is one function instead of a prop on 30 routes.
 *
 * `/admin/*` is deliberately absent — it is the internal operator console, not
 * a customer surface, and stays Broxel.
 *
 * This is not the whole picture, though: only `/marketplace`, `/marketplace/:id`,
 * `/cart` and `/order-success` actually branch through `useBrand()`. A second
 * group of components never call it at all because they are unconditionally
 * Pharma4Link and hardcode `<BrandLockup />` directly — `/login`
 * (`pages/AuthPage.tsx`), `/supplier/*` (`components/supplier/shell/PortalSidebar.tsx`,
 * `pages/SupplierLogin.tsx`, `pages/SupplierRegister.tsx`) and `/dashboard/*`
 * (`components/customer/shell/CustomerSidebar.tsx`). Whoever does the eventual host
 * split needs to touch that second group too — the entries above only cover
 * the routes that read this list.
 */
const PLATFORM_PREFIXES = [
  '/marketplace',
  '/cart',
  '/order-success',
  '/login',
  '/dashboard',
  '/supplier',
] as const

/**
 * Exact-or-followed-by-slash, not startsWith: a future `/marketplace-terms`
 * must not be swallowed by the `/marketplace` prefix.
 */
export const brandForPath = (pathname: string): Brand =>
  PLATFORM_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
    ? 'pharma4link'
    : 'broxel'

export const useBrand = (): Brand => brandForPath(useLocation().pathname)
